import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, BaseModelObject, ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumActivityLogType } from "./enums/enumActivityLogType.generated";
import { EnumBmEntityType } from "./enums/enumBmEntityType.generated";

// Model Objects

export class ActivityLogEntry extends ModelObject<ActivityLogEntry> {

    public id: Guid = Guid.createEmpty();
    /**
    * Type of the entity with which the task is associated, if any. Association is only supported to entity types which are tenant-level with a single guid id.
    */
    public activityLogType: EnumActivityLogType = EnumActivityLogType.none;
    /**
    * Type of the entity on which the activity occurred. Currently Outlet or Lead.
    */
    public activityOnEntityType: EnumBmEntityType = EnumBmEntityType.none;
    /**
    * Id of the entity on which the activity occurred. (Currently an OutletId or LeadId).
    */
    public activityOnEntityId: Guid = Guid.createEmpty();
    /**
    * Type of the entity which caused the log entry. Association is only supported to entity types which are tenant-level with a single guid id.
    */
    public relatedBmEntityType: EnumBmEntityType = EnumBmEntityType.none;
    private _relatedBmEntityId: Guid | null = null;
    /**
    * Id of the entity with which the task is associated, if any.
    */
    public get relatedBmEntityId(): Guid | null { return this._relatedBmEntityId }
    public set relatedBmEntityId(value: Guid | null) {
      this._relatedBmEntityId = value === undefined ? null : value;
    }
    /**
    * Timestamp at which the activity occurred.
    */
    public timestamp: Instant = Instant.ofEpochMilli(0);

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.activityLogType = EnumActivityLogType.getByValue(jsonSerializedObject.activityLogType, this.activityLogType);
          this.activityOnEntityType = EnumBmEntityType.getByValue(jsonSerializedObject.activityOnEntityType, this.activityOnEntityType);
          this.activityOnEntityId = Guid.parseOrEmpty(jsonSerializedObject.activityOnEntityId);
          this.relatedBmEntityType = EnumBmEntityType.getByValue(jsonSerializedObject.relatedBmEntityType, this.relatedBmEntityType);
          this.relatedBmEntityId = jsonSerializedObject.relatedBmEntityId == null ? null : Guid.parseOrEmpty(jsonSerializedObject.relatedBmEntityId);
          this.timestamp = Instant.parse(jsonSerializedObject.timestamp);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.activityLogType = this.activityLogType.value;
        toRet.activityOnEntityType = this.activityOnEntityType.value;
        toRet.activityOnEntityId = this.activityOnEntityId.toString();
        toRet.relatedBmEntityType = this.relatedBmEntityType.value;
        toRet.relatedBmEntityId = this.relatedBmEntityId == null ? null : this.relatedBmEntityId.toString();
        toRet.timestamp = this.timestamp.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "ActivityLogEntry.Id cannot be empty"},
            {field: "activityOnEntityId", rule: (v: any) => !v.isEmpty() || "ActivityLogEntry.ActivityOnEntityId cannot be empty"},
            {field: "relatedBmEntityId", rule: (v: any) => v == null || !v.isEmpty() || "ActivityLogEntry.RelatedBmEntityId cannot be empty"},
    ];
}


export class ActivityLogFilter extends ModelObject<ActivityLogFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _logTypes: EnumActivityLogType[] | null = null;
    public get logTypes(): EnumActivityLogType[] | null { return this._logTypes }
    public set logTypes(value: EnumActivityLogType[] | null) {
      this._logTypes = value === undefined ? null : value;
    }
    private _activityOnEntityTypes: EnumBmEntityType[] | null = null;
    public get activityOnEntityTypes(): EnumBmEntityType[] | null { return this._activityOnEntityTypes }
    public set activityOnEntityTypes(value: EnumBmEntityType[] | null) {
      this._activityOnEntityTypes = value === undefined ? null : value;
    }
    private _activityOnEntityIds: Guid[] | null = null;
    public get activityOnEntityIds(): Guid[] | null { return this._activityOnEntityIds }
    public set activityOnEntityIds(value: Guid[] | null) {
      this._activityOnEntityIds = value === undefined ? null : value;
    }
    private _fromTimestamp: Instant | null = null;
    /**
    * Minimum (inclusive) timestamp.
    */
    public get fromTimestamp(): Instant | null { return this._fromTimestamp }
    public set fromTimestamp(value: Instant | null) {
      this._fromTimestamp = value === undefined ? null : value;
    }
    private _toTimestamp: Instant | null = null;
    /**
    * Maximum (inclusive) timestamp.
    */
    public get toTimestamp(): Instant | null { return this._toTimestamp }
    public set toTimestamp(value: Instant | null) {
      this._toTimestamp = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.logTypes = jsonSerializedObject.logTypes == null ? null : jsonSerializedObject.logTypes.map((v: any) => EnumActivityLogType.getByValue(v));
          this.activityOnEntityTypes = jsonSerializedObject.activityOnEntityTypes == null ? null : jsonSerializedObject.activityOnEntityTypes.map((v: any) => EnumBmEntityType.getByValue(v));
          this.activityOnEntityIds = jsonSerializedObject.activityOnEntityIds == null ? null : jsonSerializedObject.activityOnEntityIds.map((v: any) => Guid.parseOrEmpty(v));
          this.fromTimestamp = jsonSerializedObject.fromTimestamp == null ? null : Instant.parse(jsonSerializedObject.fromTimestamp);
          this.toTimestamp = jsonSerializedObject.toTimestamp == null ? null : Instant.parse(jsonSerializedObject.toTimestamp);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.logTypes = this.logTypes == null ? null : this.logTypes.map(v => v.value);
        toRet.activityOnEntityTypes = this.activityOnEntityTypes == null ? null : this.activityOnEntityTypes.map(v => v.value);
        toRet.activityOnEntityIds = this.activityOnEntityIds == null ? null : this.activityOnEntityIds.map(v => v.toString());
        toRet.fromTimestamp = this.fromTimestamp == null ? null : this.fromTimestamp.toString();
        toRet.toTimestamp = this.toTimestamp == null ? null : this.toTimestamp.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredActivityLogResults extends ModelObject<FilteredActivityLogResults> {

    public matchingCount: number = 0;
    public activityLogEntries: ActivityLogEntry[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.activityLogEntries = jsonSerializedObject.activityLogEntries.map((v: any) => new ActivityLogEntry(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.activityLogEntries = this.activityLogEntries.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "activityLogEntries", rule: (v: any) => !!v || "activityLogEntries is required"},
            {field: "activityLogEntries", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as ActivityLogEntry[]).reduce((acc: Array<string>, curr: ActivityLogEntry) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class ActivityLogService extends ServiceBase {

  public async getActivityLogEntriesByFilter(tenantId: Guid, filter: ActivityLogFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredActivityLogResults> {
    const req = {
    tenantId:tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredActivityLogResults(response.results);;
    return this.makeJsonRequest("activityLog/v1/getActivityLogEntriesByFilter", req, false, extractor, opts);
  };

  public async getMostRecentActivityLogEntries(tenantId: Guid, activityLogType: EnumActivityLogType, activityOnEntityType: EnumBmEntityType, activityOnEntityIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ActivityLogEntry[]> {
    const req = {
    tenantId:tenantId.toString(),
    activityLogType:activityLogType.value,
    activityOnEntityType:activityOnEntityType.value,
    activityOnEntityIds:activityOnEntityIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.activityLogEntries.map((v: any) => new ActivityLogEntry(v));;
    return this.makeJsonRequest("activityLog/v1/getMostRecentActivityLogEntries", req, false, extractor, opts);
  };
}

export const activityLogService = new ActivityLogService();